"use client";

import { Activity, Brain, Compass, Flame, ChartLine as LineChart, Radar } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Compliance() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const capabilities = [{
    number: "01",
    title: "Global Privacy Compliance",
    description: "Strict adherence to GDPR, CCPA, and international data sovereignty laws to ensure your digital footprint remains secure.",
    Icon: LineChart
  }, {
    number: "02",
    title: "Threat Intelligence",
    description: "Advanced monitoring of emerging cyber threats, providing proactive defense mechanisms against evolving digital risks.",
    Icon: Brain
  }, {
    number: "03",
    title: "Encryption Standards",
    description: "Implementation of military-grade AES-256 encryption protocols to protect sensitive communications and data transfers.",
    Icon: Radar
  }, {
    number: "04",
    title: "Network Integrity",
    description: "Continuous auditing of infrastructure to prevent unauthorized access and maintain robust, reliable connectivity.",
    Icon: Compass
  }, {
    number: "05",
    title: "User Anonymity",
    description: "Advanced obfuscation techniques designed to mask identity and prevent tracking across all online activities.",
    Icon: Activity
  }, {
    number: "06",
    title: "Security Auditing",
    description: "Comprehensive periodic reviews of security policies, ensuring full compliance with industry-leading safety benchmarks.",
    Icon: Flame
  }];
  return <section data-section-id="4553" id="complianceandsecuritystandards" className="bg-background text-foreground py-20 md:py-28 px-4 sm:px-6 lg:px-10">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 mb-16 md:mb-20 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-5">
            <p className="font-mono text-xs tracking-widest uppercase text-primary mb-6">
              03 / Standards
            </p>
            <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-foreground">
              Security, fully verified.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pl-8 flex items-end">
            <p className="font-default text-base md:text-lg leading-relaxed text-muted-foreground/90 max-w-2xl">
              Our framework integrates rigorous compliance protocols with advanced security engineering. Every standard we uphold serves as a foundational layer in our commitment to your digital privacy and operational safety.
            </p>
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border/60">
          {capabilities.map((cap, i) => {
          const Icon = cap.Icon;
          return <div data-index={i} key={cap.number} className="group relative bg-background border border-transparent hover:border-primary/40 transition-all duration-500 ease-out p-8 sm:p-10 flex flex-col" style={{
            aspectRatio: "4 / 5",
            minHeight: "420px"
          }}>
                {}
                <span className="font-mono text-xs tracking-widest text-primary">
                  {cap.number}
                </span>

                {}
                <div className="relative flex items-center justify-start mt-10 mb-10">
                  <div aria-hidden="true" className="absolute -inset-6 rounded-lg bg-primary/15 blur-2xl opacity-60 group-hover:opacity-100 transition-opacity duration-500" />
                  <Icon strokeWidth={1} className="relative size-8 text-foreground/90 group-hover:text-primary transition-colors duration-500" />
                </div>

                {}
                <h3 className="font-serif font-normal text-2xl leading-snug text-foreground mb-4">
                  {cap.title}
                </h3>

                {}
                <p className="font-default text-[15px] leading-relaxed text-muted-foreground/80 mb-8">
                  {cap.description}
                </p>

                {}
                <div className="mt-auto">
                  <Link to="/LegalPagesPage" className="inline-flex items-center gap-2 font-mono text-xs tracking-widest uppercase text-foreground/80 hover:text-primary transition-colors duration-300">
                    <span className="relative">
                      <span className="bg-left-bottom bg-gradient-to-r from-primary to-primary bg-[length:0%_1px] bg-no-repeat group-hover:bg-[length:100%_1px] transition-[background-size] duration-500 ease-out pb-0.5">
                        Details
                      </span>
                    </span>
                    <span aria-hidden="true">→</span>
                  </Link>
                </div>
              </div>;
        })}
        </div>

        {}
        <div className="mt-16 md:mt-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pt-10 border-t border-border">
          <p className="font-default text-sm text-muted-foreground/80 max-w-xl">
            Protect your digital assets with VPNGENIX's certified security standards.
          </p>
          <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-mono text-xs tracking-widest uppercase">
            <Link to="https://vpngenix.com/go/NordVPN" newTab>View Compliance</Link>
          </Button>
        </div>
      </div>
    </section>;
}